import { Json } from '@eagle/common';
import { Account, AccountTemplate, CreateAccountFromTemplateRequest, LifecycleTemplate, SharedThing, Stage, StagePropertyDefinition, ThingLifeCycleStateResponse } from '@eagle/core-data-types';
import { ReactNode, Reducer } from 'react';
import { Nullable, Undefinable } from '../../../types';
import { UserBasicType } from '../../multiple-user-basic-editor';
import { selectHighestPriorityStakeholderRoles } from './stakeholder-utils';

export interface StakeholderState {
  account?: Nullable<Account>;
  accountId?: Nullable<string>;
  accountTemplateIds: string[];
  allowAccountRequests: boolean;
  canEdit: boolean;
  createAccount?: {
    accountTemplateId: string;
    data: CreateAccountFromTemplateRequest;
    createdAccountId?: string;
    isCustomHomeDomain: boolean;
  };
  initialAccountId?: string;
  isEditing: boolean;
  isDirty: boolean;
  isCreatingNewAccount: boolean;
  isRemoveConfirmationChecked: boolean;
  isRequired: boolean;
  lastAccountSearch?: string;
  role: string;
  config?: Omit<StakeholderConfig, 'removeConfirmation'>;
  areUsersValid: boolean;
}

interface RemoveConfirmation {
  checkboxLabel: string;
  confirmText: string;
}

interface StakeholderConfig {
  removeConfirmation?: RemoveConfirmation;
  requiredToChange?: boolean;
  selectPlaceholder?: string;
}

export interface StageConfirmDialogConfig {
  renderConfirmation?: (state: StageConfirmDialogState) => ReactNode;
  stakeholderRolesToDisplay?: string[];
  simplifiedAccountSelect?: boolean;
  stakeholders?: Record<string, StakeholderConfig>;
  successMessage?: string;
  updateStageLabel?: string;
  isSelfClaim?: boolean;
  selectedSharedThing?: SharedThing;
  skipStakeholderSelection?: boolean;
  forceEditableStakeholderSelect?: boolean;
}

export enum StageConfirmDialogStep {
  edit = 0,
  confirm = 1,
  confirmRemove = 2,
}

export interface LeasingPropertiesDefinitions {
  definitions: {
    startDate?: StagePropertyDefinition;
    finishDate?: StagePropertyDefinition;
    [key: string]: StagePropertyDefinition | undefined;
  };
  order: string[];
  requiredProperties?: string[];
}

export interface StagePropertyValue {
  value: Json;
}

export interface LeasingProperties {
  startDate?: StagePropertyValue;
  finishDate?: StagePropertyValue;
  [key: string]: StagePropertyValue | undefined;
}

export interface StakeholderMapping {
  [role: string]: string;
}

export interface LeasingAddOn {
  state: 'available' | 'pending' | 'leased';
  stakeholderMapping?: StakeholderMapping;
}

export interface AddOns {
  loan?: LeasingAddOn;
  [key: string]: unknown;
}

export interface StageProperties {
  loan?: LeasingProperties;
  [key: string]: LeasingProperties | Record<string, StagePropertyValue> | undefined;
}

export interface StageConfirmDialogState {
  stakeholders: StakeholderState[];
  confirmationChecked: boolean;
  isSubmitting: boolean;
  removeConfirmation?: RemoveConfirmation;
  config?: StageConfirmDialogConfig;
  currentStep: StageConfirmDialogStep;
  isSimplifiedAccountSelect: boolean;
  renderConfirmation?: (state: StageConfirmDialogState) => ReactNode;
  stageProperties?: StageProperties;
  requiredProperties?: Record<string, Json>;
  addOns?: AddOns;
  roleTied?: boolean;
  skippedEditStep?: boolean;
}

export type StageConfirmDialogAction =
  { type: 'SET_STEP'; step: StageConfirmDialogStep } |
  { type: 'SET_ACCOUNT'; role: string; account: Nullable<Account>; accountId: Nullable<string> } |
  { type: 'START_SUBMIT' } |
  { type: 'SUBMIT_FINISHED' } |
  { type: 'SET_CONFIRMATION'; value: boolean } |
  { type: 'START_STAKEHOLDER_EDIT_MODE'; role: string } |
  { type: 'CANCEL_STAKEHOLDER_EDIT_MODE'; role: string } |
  { type: 'FINISH_STAKEHOLDER_EDIT_MODE'; role: string } |
  { type: 'SET_LAST_ACCOUNT_SEARCH'; role: string; search: string } |
  { type: 'START_STAKEHOLDER_CREATE_MODE'; role: string; accountTemplates: AccountTemplate[]; account: Account } |
  { type: 'CANCEL_STAKEHOLDER_CREATE_MODE'; role: string } |
  { type: 'SET_STAKEHOLDER_USER_VALIDITY'; role: string; isValid: boolean } |
  { type: 'SET_ACCOUNT_TEMPLATE'; role: string; accountTemplate: AccountTemplate; account: Account } |
  { type: 'SET_ACCOUNT_TYPE'; role: string; accountTypeId: string } |
  { type: 'SET_CREATE_ACCOUNT_DATA'; role: string; field: keyof CreateAccountFromTemplateRequest; value: Json | UserBasicType[] } |
  { type: 'SET_CREATED_ACCOUNT_ID'; role: string; createdAccountId: string } |
  { type: 'SET_REMOVE_CONFIRMATION'; role: string; value: boolean } |
  { type: 'SET_STAGE_PROPERTY'; path: string; value: Json } |
  { type: 'SET_REQUIRED_PROPERTY'; path: string; value: Json }

export const stageConfirmDialogReducer: Reducer<StageConfirmDialogState, StageConfirmDialogAction> = (state, action) => {
  switch (action.type) {
    case 'SET_STEP':
      return {
        ...state,
        currentStep: action.step,
      };

    case 'SET_ACCOUNT': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => ({ ...stakeholder, account: action.account, accountId: action.accountId, isEditing: false, isDirty: true, areUsersValid: true }),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'START_SUBMIT':
      return {
        ...state,
        isSubmitting: true,
      };

    case 'SET_CONFIRMATION':
      return {
        ...state,
        confirmationChecked: action.value,
      };

    case 'SUBMIT_FINISHED':
      return {
        ...state,
        isSubmitting: false,
      };

    case 'START_STAKEHOLDER_EDIT_MODE': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => ({ ...stakeholder, isEditing: true }),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'CANCEL_STAKEHOLDER_EDIT_MODE': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => ({ ...stakeholder, isEditing: false, isDirty: false, accountId: stakeholder.initialAccountId, areUsersValid: true }),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'FINISH_STAKEHOLDER_EDIT_MODE': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => ({ ...stakeholder, isEditing: false }),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_LAST_ACCOUNT_SEARCH': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => ({ ...stakeholder, lastAccountSearch: action.search }),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'START_STAKEHOLDER_CREATE_MODE': {
      const updatedStakeholders = updateStakeholderState(state.stakeholders, action.role, (stakeholder) => {
        if (action.accountTemplates.length === 1) {
          return {
            ...stakeholder,
            isCreatingNewAccount: true,
            isEditing: false,
            createAccount: {
              accountTemplateId: action.accountTemplates[0]._id,
              data: createInitialAccountData(action.accountTemplates[0], stakeholder.lastAccountSearch),
              isCustomHomeDomain: action.accountTemplates[0].accountDefaults.homeDomain.type === 'custom',
            },
          };
        }
        return { ...stakeholder, isCreatingNewAccount: true, isEditing: false };
      });

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'CANCEL_STAKEHOLDER_CREATE_MODE': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => {
          const updatedStakeholder: StakeholderState = { ...stakeholder, isCreatingNewAccount: false, createAccount: undefined };
          if (stakeholder.isEditing) {
            updatedStakeholder.accountId = updatedStakeholder.initialAccountId;
            updatedStakeholder.isDirty = false;
          }
          if (updatedStakeholder.initialAccountId || updatedStakeholder.accountId) {
            updatedStakeholder.areUsersValid = true;
          }

          return updatedStakeholder;
        },
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_STAKEHOLDER_USER_VALIDITY': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => (
          { ...stakeholder, areUsersValid: action.isValid }
        ),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_ACCOUNT_TEMPLATE': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => {
          return {
            ...stakeholder,
            createAccount: {
              accountTemplateId: action.accountTemplate._id,
              data: createInitialAccountData(action.accountTemplate, stakeholder.lastAccountSearch),
              isCustomHomeDomain: action.accountTemplate.accountDefaults.homeDomain.type === 'custom',
            },
          };
        },
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_ACCOUNT_TYPE': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => {
          if (!stakeholder.createAccount) {
            return stakeholder;
          }
          return { ...stakeholder, createAccount: { ...stakeholder.createAccount, data: { ...stakeholder.createAccount.data, accountTypeId: action.accountTypeId, properties: {} } } };
        },
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_CREATE_ACCOUNT_DATA': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => {
          if (!stakeholder.createAccount) {
            return stakeholder;
          }
          return { ...stakeholder, createAccount: { ...stakeholder.createAccount, data: { ...stakeholder.createAccount.data, [action.field]: action.value } } };
        },
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_CREATED_ACCOUNT_ID': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => {
          if (stakeholder.createAccount) {
            return {
              ...stakeholder,
              createAccount: {
                ...stakeholder.createAccount,
                createdAccountId: action.createdAccountId,
              },
            };
          }
          return stakeholder;
        },
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_REMOVE_CONFIRMATION': {
      const updatedStakeholders = updateStakeholderState(
        state.stakeholders,
        action.role,
        (stakeholder) => ({ ...stakeholder, isRemoveConfirmationChecked: action.value }),
      );

      return {
        ...state,
        stakeholders: updatedStakeholders,
      };
    }

    case 'SET_STAGE_PROPERTY': {
      const pathParts = action.path.split('.');

      if (pathParts.length !== 2) {
        return state;
      }

      const propertyCategory = pathParts[0];
      const propertyName = pathParts[1];

      if (!propertyCategory || !propertyName) {
        return state;
      }

      const currentProperties = state.stageProperties || {};

      if (propertyCategory === 'loan') {
        const loanProps: LeasingProperties = {
          ...(currentProperties.loan || {}),
        };

        if (!loanProps[propertyName]) {
          loanProps[propertyName] = { value: action.value };
        } else if (loanProps[propertyName]) {
          loanProps[propertyName] = {
            ...loanProps[propertyName],
            value: action.value,
          };
        }

        return {
          ...state,
          stageProperties: {
            ...currentProperties,
            loan: loanProps,
          },
        };
      }

      const categoryProps = currentProperties[propertyCategory] as Record<string, StagePropertyValue> || {};

      return {
        ...state,
        stageProperties: {
          ...currentProperties,
          [propertyCategory]: {
            ...categoryProps,
            [propertyName]: { value: action.value },
          },
        },
      };
    }

    case 'SET_REQUIRED_PROPERTY': {
      return {
        ...state,
        requiredProperties: {
          ...(state.requiredProperties || {}),
          [action.path]: action.value,
        },
      };
    }

    default:
      return state;
  }
};

const updateStakeholderState = (
  stakeholderStates: StakeholderState[],
  role: string,
  updater: (stakeholder: StakeholderState) => StakeholderState,
): StakeholderState[] => {
  const stakeholderIndex = stakeholderStates.findIndex((s) => s.role === role);
  if (stakeholderIndex === -1) {
    return stakeholderStates;
  }

  const updatedStakeholders = [...stakeholderStates];
  updatedStakeholders[stakeholderIndex] = updater(updatedStakeholders[stakeholderIndex]);

  return updatedStakeholders;
};

const determineTargetStage = (
  lifecycleTemplate: LifecycleTemplate,
  selectedSharedThing: SharedThing | undefined,
  stakeholderRoles: string[],
  config?: StageConfirmDialogConfig,
  stage?: Stage,
): Stage => {
  if (!config?.isSelfClaim) return stage || lifecycleTemplate.stages[0];

  if (!selectedSharedThing) return lifecycleTemplate.stages[0];

  const currentStageId = selectedSharedThing.lifecycleState?.stageId;
  const currentStage = lifecycleTemplate.stages.find((s) => s.stageId === currentStageId);

  const currentUserRoles = selectHighestPriorityStakeholderRoles(
    stakeholderRoles,
    lifecycleTemplate,
    config?.selectedSharedThing,
    Boolean(config?.isSelfClaim),
  );
  const currentUserRole = currentUserRoles.length === 1 ? currentUserRoles[0] : undefined;
  const selfClaimTargetStageId = currentUserRole ? currentStage?.stakeholders?.[currentUserRole]?.selfClaim?.targetStage : undefined;

  return selfClaimTargetStageId
    ? lifecycleTemplate.stages.find((s) => s.stageId === selfClaimTargetStageId) || lifecycleTemplate.stages[0]
    : lifecycleTemplate.stages[0];
};

const applyStakeholderDefaults = (
  stakeholders: StakeholderState[],
  lifeCycleState: ThingLifeCycleStateResponse,
  stageId: string,
  defaultsConfig: Record<string, Record<string, string>>,
  lifecycleTemplate: LifecycleTemplate,
): StakeholderState[] => {
  const stageDefaults = defaultsConfig[stageId];
  if (!stageDefaults) return stakeholders;

  return stakeholders.map((stakeholder) => {
    const defaultAccountSource = stageDefaults[stakeholder.role];

    if (!defaultAccountSource || stakeholder.initialAccountId) {
      return stakeholder;
    }

    const isStakeholderRole = defaultAccountSource in lifecycleTemplate.stakeholderAccounts;
    const sourceAccountId = isStakeholderRole ? lifeCycleState.stakeholders?.[defaultAccountSource]?._id : defaultAccountSource;

    if (!sourceAccountId) {
      return stakeholder;
    }

    return {
      ...stakeholder,
      accountId: sourceAccountId,
      initialAccountId: sourceAccountId,
      isDirty: true,
    };
  });
};

export const initStageConfirmDialogReducer = ({
  lifeCycleState,
  lifecycleTemplate,
  stakeholderRoles,
  config,
  stage,
  editableStakeholders,
  lifeCycleStakeholderDefaults,
}: {
  lifeCycleState: ThingLifeCycleStateResponse;
  lifecycleTemplate: LifecycleTemplate;
  stakeholderRoles: string[];
  config?: StageConfirmDialogConfig;
  stage?: Stage;
  editableStakeholders: string[];
  lifeCycleStakeholderDefaults: Undefinable<Record<string, Record<string, string>>>;
}): StageConfirmDialogState => {
  const targetStage = determineTargetStage(
    lifecycleTemplate,
    config?.selectedSharedThing,
    stakeholderRoles,
    config,
    stage,
  );

  const requiredStakeholders = targetStage.enterConditions?.requiredStakeholders ?? [];

  const currentUserRoles = selectHighestPriorityStakeholderRoles(
    stakeholderRoles,
    lifecycleTemplate,
    config?.selectedSharedThing,
    Boolean(config?.isSelfClaim),
  );
  const roleTied = currentUserRoles.length > 1;
  const currentUserRole = currentUserRoles.length === 1 ? currentUserRoles[0] : undefined;

  const isCurrentUserOnlyRequiredStakeholder =
    requiredStakeholders.length === 1 &&
    requiredStakeholders[0].toLowerCase() === currentUserRole;

  const stageStakeholderRoles = Object.keys(targetStage.stakeholders || {});
  const allStakeholderRoles = Array.from(new Set([
    ...stageStakeholderRoles,
    ...requiredStakeholders,
  ]));

  let stakeholderStates: StakeholderState[] = allStakeholderRoles
    .filter((role) => {
      if (config?.isSelfClaim) {
        if (requiredStakeholders.includes(role)) {
          return true;
        }
        if (config?.stakeholderRolesToDisplay) {
          return config.stakeholderRolesToDisplay.includes(role);
        }
        if (isCurrentUserOnlyRequiredStakeholder && role.toLowerCase() === currentUserRole) {
          return false;
        }
        if (requiredStakeholders.includes(role)) {
          return true;
        }
        return role.toLowerCase() !== currentUserRole;
      }

      if (!config?.stakeholderRolesToDisplay) {
        return true;
      }
      return config.stakeholderRolesToDisplay.includes(role);
    })
    .map((role) => {
      const stakeholder = targetStage.stakeholders?.[role];
      const stakeholderConfig = config?.stakeholders?.[role];

      const initialAccountId = lifeCycleState.stakeholders?.[role]?._id ??
        lifecycleTemplate.stakeholderAccounts[role]?.fixedAccountId ?? undefined;

      return {
        role,
        initialAccountId,
        allowAccountRequests: config?.simplifiedAccountSelect ? false : Boolean(stakeholder?.allowAccountRequest),
        accountTemplateIds: lifecycleTemplate.stakeholderAccounts[role]?.accountTemplateIds ?? [],
        isEditing: false,
        isDirty: false,
        accountId: initialAccountId,
        isCreatingNewAccount: false,
        isRemoveConfirmationChecked: false,
        isRequired: Boolean(targetStage.enterConditions?.requiredStakeholders?.includes(role)),
        canEdit: !lifecycleTemplate.stakeholderAccounts[role]?.fixedAccountId &&
          editableStakeholders.includes(role),
        config: stakeholderConfig,
        areUsersValid: true,
      };
    });

  if (lifeCycleStakeholderDefaults && lifeCycleStakeholderDefaults[targetStage.stageId]) {
    stakeholderStates = applyStakeholderDefaults(
      stakeholderStates,
      lifeCycleState,
      targetStage.stageId,
      lifeCycleStakeholderDefaults,
      lifecycleTemplate,
    );
  }

  const hasRequiredStakeholdersNeedingSelection = stakeholderStates.some(
    (s) => s.isRequired && !s.initialAccountId,
  );

  const shouldSkipToConfirm = config?.isSelfClaim && (
    !hasRequiredStakeholdersNeedingSelection || isCurrentUserOnlyRequiredStakeholder
  );

  const removeConfirmation = (() => {
    if (stakeholderStates.length === 1) {
      const role = stakeholderStates[0].role;
      const stakeholderConfig = config?.stakeholders?.[role];
      return stakeholderConfig?.removeConfirmation;
    }
    return undefined;
  })();

  const finalState = {
    stakeholders: stakeholderStates,
    confirmationChecked: false,
    isSubmitting: false,
    removeConfirmation,
    config,
    currentStep: shouldSkipToConfirm || stakeholderStates.length === 0
      ? StageConfirmDialogStep.confirm
      : StageConfirmDialogStep.edit,
    isSimplifiedAccountSelect: Boolean(config?.simplifiedAccountSelect),
    renderConfirmation: config?.renderConfirmation,
    stageProperties: initializeStageProperties(targetStage, lifeCycleState),
    requiredProperties: initializeRequiredProperties(targetStage, config?.selectedSharedThing),
    addOns: initializeAddOns(targetStage),
    roleTied,
    skippedEditStep: shouldSkipToConfirm || stakeholderStates.length === 0,
  };

  return finalState;
};

const createInitialAccountData = (accountTemplate: AccountTemplate, lastAccountSearch?: string): CreateAccountFromTemplateRequest => {
  const accountTypeId = accountTemplate.accountDefaults.accountTypeIds.length === 1 ? accountTemplate.accountDefaults.accountTypeIds[0] : null;

  return {
    accountTypeId,
    display: lastAccountSearch?.trim() ?? '',
    users: [{ email: '', display: '' }],
    homeDomain: accountTemplate.accountDefaults.homeDomain.type === 'custom' ? '' : null,
    labels: accountTemplate.accountDefaults.labels,
    properties: {},
    tags: accountTemplate.accountDefaults.tags,
  };
};

const initializeStageProperties = (
  targetStage: Stage,
  lifeCycleState: ThingLifeCycleStateResponse,
): StageProperties | undefined => {
  if (!targetStage.stageProperties) {
    return undefined;
  }

  const stageProperties: StageProperties = {};

  const shouldPersistProperty = (persistConfig: unknown, stageId: string): boolean => {
    if (!persistConfig) return false;

    if (typeof persistConfig === 'object' && persistConfig !== null) {
      return Boolean((persistConfig as Record<string, boolean>)[stageId]);
    } else if (Array.isArray(persistConfig)) {
      return persistConfig.includes(stageId);
    }

    return false;
  };

  for (const [scopeName, scopeDefinition] of Object.entries(targetStage.stageProperties)) {
    if (!targetStage.addOns?.[scopeName]) {
      continue;
    }

    const scopeProperties: Record<string, StagePropertyValue> = {};

    const order = scopeDefinition.order || [];
    const definitions = scopeDefinition.definitions || {};

    for (const propertyName of order) {
      const definition = definitions[propertyName];
      const existingProperty = lifeCycleState.stageProperties?.[scopeName]?.[propertyName];

      if (existingProperty && typeof existingProperty === 'object' && 'value' in existingProperty) {
        scopeProperties[propertyName] = { value: existingProperty.value };
      } else if (definition?.persistFromPreviousStage && lifeCycleState.stageId) {
        const currentStageId = lifeCycleState.stageId;

        if (shouldPersistProperty(definition.persistFromPreviousStage, currentStageId)) {
          const previousPropertyValue = lifeCycleState.stageProperties?.[scopeName]?.[propertyName];

          if (previousPropertyValue &&
            typeof previousPropertyValue === 'object' &&
            'value' in previousPropertyValue &&
            previousPropertyValue.value !== undefined) {
            scopeProperties[propertyName] = {
              value: previousPropertyValue.value,
            };
          }
        }
      }
    }

    if (Object.keys(scopeProperties).length > 0) {
      stageProperties[scopeName] = scopeProperties;
    }
  }

  return Object.keys(stageProperties).length > 0 ? stageProperties : undefined;
};

const initializeRequiredProperties = (
  targetStage: Stage,
  selectedSharedThing?: SharedThing,
): Record<string, Json> | undefined => {
  if (!targetStage.enterConditions?.requiredProperties ||
    Object.keys(targetStage.enterConditions.requiredProperties).length === 0) {
    return undefined;
  }

  const requiredProperties: Record<string, Json> = {};
  const requiredKeys = Object.keys(targetStage.enterConditions.requiredProperties);

  if (selectedSharedThing) {
    for (const key of requiredKeys) {
      if (selectedSharedThing.properties[key]) {
        requiredProperties[key] = selectedSharedThing.properties[key];
      }
    }
  }

  return Object.keys(requiredProperties).length > 0 ? requiredProperties : undefined;
};

const initializeAddOns = (
  targetStage: Stage,
): AddOns | undefined => {
  if (!targetStage.addOns) {
    return undefined;
  }

  const addOns: AddOns = {};

  if (targetStage.addOns.loan) {
    addOns.loan = {
      state: targetStage.addOns.loan.state as 'available' | 'pending' | 'leased',
      stakeholderMapping: targetStage.addOns.loan.stakeholderMapping as StakeholderMapping,
    };
  }

  return Object.keys(addOns).length > 0 ? addOns : undefined;
};
